import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Plus, Trash2, ShieldAlert, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { callingAPI } from '../api';

const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const SHORT = { MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed', THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat', SUNDAY: 'Sun' };
const ZONE_ID = 'Asia/Kolkata';
const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

const STATUS_STYLE = {
  ACTIVE: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300',
  MISSING: 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
  INVALID: 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300',
};

const REASON_TEXT = {
  NO_POLICY_CONFIGURED: 'No schedule saved yet — unknown numbers stay hidden (fail-closed).',
  INVALID_ZONE_ID: 'Saved timezone is not readable — unknown numbers stay hidden (fail-closed).',
  INVALID_WINDOW: 'A saved window is malformed — unknown numbers stay hidden (fail-closed).',
  OUTSIDE_WORKING_HOURS: 'Outside working hours right now — the app shows “Unknown Number”.',
  REVEAL_DISABLED: 'Reveal is switched off — the app shows “Unknown Number” at all times.',
  ALWAYS_UNKNOWN_MODE: 'Always Unknown is on — every incoming call shows “Unknown Number”, even known leads inside working hours.',
};

export const WorkPolicyEditor = ({ employeeId, employeeName, canEdit }) => {
  const [policy, setPolicy] = useState(null);
  const [windows, setWindows] = useState([]);
  const [reveal, setReveal] = useState(false);
  const [mode, setMode] = useState('WORKING_HOURS');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await callingAPI.getWorkPolicy(employeeId);
      setPolicy(data);
      setWindows((data.windows || []).map((w) => ({ ...w })));
      setReveal(!!data.reveal_unknown_during_working_hours);
      setMode(data.policy_status === 'ACTIVE' ? (data.unknown_number_mode || 'WORKING_HOURS') : 'WORKING_HOURS');
    } catch (e) {
      setError(e?.response?.data?.detail?.message || 'Could not load the working-hours policy');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const addWindow = (day) => setWindows((w) => [...w, { day, starts_at: '09:30', ends_at: '18:30' }]);
  const removeWindow = (i) => setWindows((w) => w.filter((_, idx) => idx !== i));
  const patchWindow = (i, key, value) => setWindows((w) => w.map((x, idx) => (idx === i ? { ...x, [key]: value } : x)));

  const invalid = windows.some((w) => !HHMM.test(w.starts_at || '') || !HHMM.test(w.ends_at || ''));

  const save = async () => {
    if (invalid) { toast.error('Each window needs a valid start and end time (HH:mm).'); return; }
    setSaving(true);
    try {
      // Exact backend contract: zone_id, windows[].{day,starts_at,ends_at}, reveal_unknown_during_working_hours
      const { data } = await callingAPI.setWorkPolicy(employeeId, {
        zone_id: ZONE_ID,
        windows: windows.map((w) => ({ day: w.day, starts_at: w.starts_at, ends_at: w.ends_at })),
        reveal_unknown_during_working_hours: reveal,
        unknown_number_mode: mode,
        // Version this form was loaded with - a stale tab can never overwrite a newer schedule.
        expected_policy_version: policy?.policy_version ?? 0,
      });
      setPolicy(data);
      setWindows((data.windows || []).map((w) => ({ ...w })));
      setReveal(!!data.reveal_unknown_during_working_hours);
      toast.success(`Working hours saved (version ${data.policy_version})`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'POLICY_VERSION_CONFLICT') {
        toast.error('This schedule was changed elsewhere — reloading the latest version.');
        await load();
      } else {
        toast.error(typeof d === 'string' ? d : d?.message || 'Could not save the working hours');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-6 text-center text-sm text-muted-foreground" data-testid="work-policy-loading">
      <Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>;
  }
  if (error) {
    return <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:bg-rose-900/20 dark:text-rose-300" data-testid="work-policy-error">{error}</p>;
  }

  const status = policy?.policy_status || 'MISSING';
  const allowed = !!policy?.unknown_reveal_allowed;
  const alwaysUnknown = mode === 'ALWAYS_UNKNOWN';
  const windowsLocked = !canEdit || alwaysUnknown;

  return (
    <div className="space-y-3" data-testid="work-policy-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-medium"><Clock className="h-4 w-4 text-primary" /> Working Hours &amp; Number Masking</h3>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[status] || STATUS_STYLE.MISSING}`} data-testid="work-policy-status">
          {status === 'ACTIVE' ? `Active · v${policy.policy_version}` : status === 'MISSING' ? 'Not configured' : 'Invalid policy'}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="rounded bg-slate-100 px-2 py-0.5 dark:bg-slate-800" data-testid="work-policy-zone">Timezone: {ZONE_ID}</span>
        <span data-testid="work-policy-reveal-state" className={`flex items-center gap-1 ${allowed ? 'text-emerald-600' : 'text-amber-600'}`}>
          {allowed ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
          Unknown numbers right now: {alwaysUnknown ? 'always hidden' : allowed ? 'revealed' : 'hidden'}
        </span>
      </div>
      {policy?.reason && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-muted-foreground dark:bg-slate-800/60" data-testid="work-policy-reason">
          {REASON_TEXT[policy.reason] || policy.reason}
        </p>
      )}

      <div className="space-y-1" data-testid="work-policy-mode">
        <label className="text-xs font-medium">Incoming number display</label>
        <select value={mode} disabled={!canEdit} onChange={(e) => setMode(e.target.value)}
          data-testid="work-policy-mode-select"
          className="w-full rounded border border-slate-200 bg-transparent px-2 py-1.5 text-xs dark:border-slate-700 disabled:opacity-50">
          <option value="WORKING_HOURS">Working hours — reveal only inside the schedule below</option>
          <option value="ALWAYS_UNKNOWN">Always Unknown — never show any incoming number</option>
        </select>
        {alwaysUnknown && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300" data-testid="work-policy-always-unknown-note">
            Every incoming call shows “Unknown Number”, including known leads and inside working hours. The
            schedule below stays saved and becomes active again when you switch back to Working hours.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-1" data-testid="work-policy-day-picker">
        {DAYS.map((d) => (
          <button key={d} type="button" disabled={windowsLocked} onClick={() => addWindow(d)} data-testid={`work-policy-add-${d}`}
            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${windows.some((w) => w.day === d)
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-slate-200 text-muted-foreground hover:border-primary hover:text-primary dark:border-slate-700'} disabled:opacity-50`}>
            <Plus className="mr-1 inline h-3 w-3" />{SHORT[d]}
          </button>
        ))}
      </div>

      {windows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-muted-foreground dark:border-slate-700" data-testid="work-policy-empty">
          No working windows. Unknown numbers stay hidden until a window is added and saved.
        </p>
      ) : (
        <div className="space-y-2" data-testid="work-policy-windows">
          {windows.map((w, i) => {
            const overnight = HHMM.test(w.starts_at) && HHMM.test(w.ends_at) && w.starts_at > w.ends_at;
            return (
              <div key={`${w.day}-${i}`} data-testid={`work-policy-window-${i}`}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-2 dark:border-slate-700">
                <select value={w.day} disabled={windowsLocked} onChange={(e) => patchWindow(i, 'day', e.target.value)}
                  data-testid={`work-policy-day-${i}`} className="rounded border border-slate-200 bg-transparent px-2 py-1 text-xs dark:border-slate-700">
                  {DAYS.map((d) => <option key={d} value={d}>{SHORT[d]}</option>)}
                </select>
                <input type="time" value={w.starts_at} disabled={windowsLocked} data-testid={`work-policy-start-${i}`}
                  onChange={(e) => patchWindow(i, 'starts_at', e.target.value)}
                  className="rounded border border-slate-200 bg-transparent px-2 py-1 text-xs dark:border-slate-700" />
                <span className="text-xs text-muted-foreground">to</span>
                <input type="time" value={w.ends_at} disabled={windowsLocked} data-testid={`work-policy-end-${i}`}
                  onChange={(e) => patchWindow(i, 'ends_at', e.target.value)}
                  className="rounded border border-slate-200 bg-transparent px-2 py-1 text-xs dark:border-slate-700" />
                {overnight && <span className="text-[11px] text-indigo-600 dark:text-indigo-400" data-testid={`work-policy-overnight-${i}`}>overnight</span>}
                {w.starts_at === w.ends_at && <span className="text-[11px] text-amber-600" data-testid={`work-policy-empty-window-${i}`}>empty window</span>}
                {canEdit && (
                  <button type="button" onClick={() => removeWindow(i)} data-testid={`work-policy-remove-${i}`}
                    className="ml-auto text-muted-foreground hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <label className={`flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700 ${alwaysUnknown ? 'opacity-50' : ''}`} data-testid="work-policy-reveal-toggle">
        <span>
          <span className="block text-sm font-medium">Reveal Unknown Numbers During Working Hours</span>
          <span className="block text-xs text-muted-foreground">
            {alwaysUnknown
              ? 'Ignored while Always Unknown is selected — numbers stay hidden at all times.'
              : 'Off, or outside the windows above, the Calling App shows “Unknown Number”.'}
          </span>
        </span>
        <input type="checkbox" className="mt-1 h-4 w-4" checked={reveal} disabled={windowsLocked}
          onChange={(e) => setReveal(e.target.checked)} />
      </label>

      {canEdit ? (
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={load} data-testid="work-policy-reload"
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs dark:border-slate-700">Reload</button>
          <button type="button" onClick={save} disabled={saving || invalid} data-testid="work-policy-save"
            className="rounded-lg bg-primary px-4 py-1.5 text-xs font-medium text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Working Hours'}
          </button>
        </div>
      ) : (
        <p className="text-right text-xs text-muted-foreground" data-testid="work-policy-readonly">
          Only a Super Admin can change {employeeName}&apos;s schedule.
        </p>
      )}
    </div>
  );
};

export default WorkPolicyEditor;
